/**
 * Outbound customer webhook delivery — shared helpers.
 *
 * Implements the protocol described in `docs/08-ergonomic-api.md` §9:
 *
 *   event         = "audit.completed"
 *   body shape    = { event, delivered_at, attempt, data: AuditResponse }
 *   signature     = X-Clickweave-Signature: t=<unix_ms>,v1=<hex_hmac_sha256>
 *   MAC input     = `${t}.${body}` (identical construction to internal
 *                   runner callbacks — single HMAC protocol across the
 *                   service, only the header name differs)
 *   replay window = 5 minutes (verifier's responsibility)
 *
 * Retry math lives here so the CF Queue consumer and the cron sweep
 * can agree on when the next attempt should fire. `WEBHOOK_DELIVERY_ATTEMPTS`
 * is the total number of attempts we make before declaring the delivery
 * dead — five is what the handover §1 specifies.
 */
import { z } from 'zod';
import { hmacSha256Hex } from './signatures.js';

/**
 * Header name emitted on the outbound POST. Distinct from the internal
 * `X-Signature` header (used by runner → webhooks callbacks) so the
 * customer-facing surface can evolve independently without clashing.
 */
export const CUSTOMER_WEBHOOK_SIGNATURE_HEADER = 'X-Clickweave-Signature';

/**
 * Event name emitted in the body `event` field. Currently the only
 * value; kept as a constant so consumers (`packages/shared` tests,
 * customer SDKs) match against it rather than a string literal.
 */
export const CUSTOMER_WEBHOOK_EVENT_AUDIT_COMPLETED = 'audit.completed' as const;
export type CustomerWebhookEvent = typeof CUSTOMER_WEBHOOK_EVENT_AUDIT_COMPLETED;

/**
 * Total number of attempts before a delivery is marked `dead`.
 * Retry 0 is the initial attempt; retries 1..N-1 follow the delay
 * schedule below.
 */
export const WEBHOOK_DELIVERY_ATTEMPTS = 5;

/**
 * Retry delays in milliseconds — index = attempt number that just FAILED.
 *
 *   attempt 0 failed → wait 1m  → try attempt 1
 *   attempt 1 failed → wait 5m  → try attempt 2
 *   attempt 2 failed → wait 25m → try attempt 3
 *   attempt 3 failed → wait 2h  → try attempt 4
 *   attempt 4 failed → mark dead
 *
 * Length N-1: the last slot would correspond to "attempt N-1 failed"
 * which skips to dead; keeping the array at N-1 rather than N means
 * callers never need to special-case an out-of-range read.
 */
export const WEBHOOK_RETRY_DELAYS_MS: readonly number[] = [
  60_000, // 1 minute
  5 * 60_000, // 5 minutes
  25 * 60_000, // 25 minutes
  2 * 60 * 60_000, // 2 hours
];

/**
 * Given the just-failed attempt number (0-indexed), return the next
 * attempt's scheduled timestamp, or `null` when no further attempt is
 * warranted (→ caller marks the delivery `dead`).
 */
export function nextAttemptAt(failedAttempt: number, nowMs: number = Date.now()): Date | null {
  if (failedAttempt < 0) return new Date(nowMs);
  const delay = WEBHOOK_RETRY_DELAYS_MS[failedAttempt];
  if (delay === undefined) return null;
  return new Date(nowMs + delay);
}

/**
 * Classify a delivery attempt outcome based on HTTP status or fetch
 * error. 2xx → success. 4xx other than 408/429 → fail-fast (dead).
 * Everything else (5xx, 408, 429, network errors) → retry.
 */
export type DeliveryOutcome =
  | { kind: 'success'; status: number }
  | { kind: 'retry'; reason: string; status?: number }
  | { kind: 'fail_fast'; reason: string; status: number };

export function classifyDeliveryResult(result: {
  status?: number;
  error?: Error | unknown;
}): DeliveryOutcome {
  if (result.error !== undefined) {
    return {
      kind: 'retry',
      reason: (result.error as Error)?.message ?? 'fetch_error',
    };
  }
  const status = result.status ?? 0;
  if (status >= 200 && status < 300) return { kind: 'success', status };
  // Retry on rate-limiting + request timeout even though 4xx — customer
  // infrastructure burps.
  if (status === 408 || status === 429) {
    return { kind: 'retry', reason: `http_${status}`, status };
  }
  if (status >= 500) {
    return { kind: 'retry', reason: `http_${status}`, status };
  }
  // 4xx other than the above is a customer-side contract failure. No
  // point retrying — mark dead on first occurrence.
  if (status >= 400) {
    return { kind: 'fail_fast', reason: `http_${status}`, status };
  }
  // 1xx / 3xx: fetch should have followed the redirect or we never got
  // here. Treat as retry-worthy.
  return { kind: 'retry', reason: `http_${status || 'unknown'}`, status };
}

/**
 * Build the `X-Clickweave-Signature` header value.
 *
 * `body` must be the exact bytes sent in the outbound POST body, and
 * `tMs` the millisecond timestamp embedded both in the header and the
 * MAC input. The convention matches `packages/shared/src/signatures.ts`
 * (runner callbacks) so customers can share verifier code.
 */
export async function signWebhookBody(
  secret: string,
  body: string,
  tMs: number = Date.now(),
): Promise<string> {
  const hex = await hmacSha256Hex(secret, `${tMs}.${body}`);
  return `t=${tMs},v1=${hex}`;
}

/**
 * Cap the response body we persist for observability. Full 4KB is
 * plenty for debugging; caller truncates upstream so the DB never
 * stores multi-MB error bodies from misbehaving customer endpoints.
 */
export const WEBHOOK_RESPONSE_EXCERPT_BYTES = 4096;

export function truncateResponseExcerpt(
  text: string,
  limit: number = WEBHOOK_RESPONSE_EXCERPT_BYTES,
): string {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}…`;
}

// ─── Queue message shape ─────────────────────────────────────────────────

/**
 * Body enqueued by the webhooks Worker onto the `webhook-deliveries`
 * CF Queue, consumed by the webhook-delivery handler in apps/consumer.
 *
 * Intentionally minimal: the consumer reads the canonical delivery row
 * from Supabase, so the message only needs to uniquely identify which
 * row to attempt. That keeps the message small (well under the 128KB
 * CF Queue limit) and lets the consumer handle reschedule-without-resend
 * by looking at `webhook_deliveries.next_attempt_at`.
 */
export const WebhookDeliveryJobSchema = z
  .object({
    /** FK to `webhook_deliveries.id`. */
    delivery_id: z.number().int().positive(),
    /** `wd-...` public id (for logging). */
    delivery_uuid: z.string(),
    /** Retry counter stamped at enqueue time. Informational. */
    attempt: z.number().int().nonnegative(),
  })
  .strict();

export type WebhookDeliveryJob = z.infer<typeof WebhookDeliveryJobSchema>;
